<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useBranding } from 'shared/composables/useBranding';

import PageHeader from '../SettingsSubPageHeader.vue';
import Icon from 'next/icon/Icon.vue';

const { t } = useI18n();
const route = useRoute();
const { replaceInstallationName } = useBranding();

const globalConfig = useMapGetter('globalConfig/get');

const ALL_CHANNEL_ICONS = [
  'i-woot-website',
  'i-woot-facebook',
  'i-woot-whatsapp',
  'i-woot-sms',
  'i-woot-mail',
  'i-woot-api',
  'i-woot-telegram',
  'i-woot-line',
  'i-woot-instagram',
  'i-ri-twitter-x-fill',
  'i-ri-linkedin-box-fill',
  'i-ri-youtube-fill',
  'i-ri-threads-fill',
  'i-ri-google-fill',
  'i-woot-tiktok',
  'i-ri-phone-fill',
];

const getIconColor = (icon) => {
  const colorMap = {
    'i-woot-website': '#3B82F6',
    'i-woot-facebook': '#1877F2',
    'i-woot-messenger': '#1877F2',
    'i-woot-whatsapp': '#25D366',
    'i-woot-instagram': '#E4405F',
    'i-woot-telegram': '#0088CC',
    'i-woot-line': '#00B900',
    'i-woot-tiktok': '#000000',
    'i-ri-twitter-x-fill': '#000000',
    'i-ri-linkedin-box-fill': '#0A66C2',
    'i-ri-youtube-fill': '#FF0000',
    'i-ri-threads-fill': '#000000',
    'i-ri-google-fill': '#4285F4',
    'i-ri-phone-fill': '#8E44AD',
    'i-woot-gmail': '#EA4335',
    'i-woot-outlook': '#0078D4',
    'i-woot-mail': '#7C3AED',
    'i-woot-sms': '#10B981',
    'i-woot-api': '#6366F1',
    'i-woot-twilio': '#F22F46',
  };
  return colorMap[icon] || null;
};

const createFlowSteps = computed(() => {
  const steps = ['CHANNEL', 'INBOX', 'AGENT', 'FINISH'];

  const routes = {
    CHANNEL: 'settings_inbox_new',
    INBOX: 'settings_inboxes_page_channel',
    AGENT: 'settings_inboxes_add_agents',
    FINISH: 'settings_inbox_finish',
  };

  return steps.map(step => {
    return {
      title: t(`INBOX_MGMT.CREATE_FLOW.${step}.TITLE`),
      body: t(`INBOX_MGMT.CREATE_FLOW.${step}.BODY`),
      route: routes[step],
    };
  });
});

const isFirstStep = computed(() => {
  return route.name === 'settings_inbox_new';
});

const isFinishStep = computed(() => {
  return route.name === 'settings_inbox_finish';
});

const pageTitle = computed(() => {
  if (isFirstStep.value) {
    return t('INBOX_MGMT.ADD.AUTH.TITLE');
  }
  if (isFinishStep.value) {
    return t('INBOX_MGMT.ADD.AUTH.TITLE_FINISH');
  }
  return t('INBOX_MGMT.ADD.AUTH.TITLE_NEXT');
});

const items = computed(() => {
  return createFlowSteps.value.map(item => ({
    ...item,
    body: replaceInstallationName(item.body),
  }));
});
</script>

<template>
  <div class="mx-2 flex flex-col gap-6 mb-8">
    <PageHeader class="block lg:hidden !mb-0" :header-title="pageTitle" />
    <div class="hidden lg:grid grid-cols-1 lg:grid-cols-8 items-center gap-2">
      <div class="col-span-2 w-full" />
      <div class="flex items-center gap-2 col-span-6 ltr:ml-8 rtl:mr-8">
        <div
          v-for="icon in ALL_CHANNEL_ICONS"
          :key="icon"
          class="size-8 bg-n-alpha-2 flex items-center flex-shrink-0 justify-center rounded-full"
        >
          <Icon :icon="icon" class="size-4" :style="getIconColor(icon) ? { color: getIconColor(icon) } : {}" />
        </div>
      </div>
    </div>
    <div
      class="grid grid-cols-1 lg:grid-cols-8 lg:divide-x lg:divide-n-weak rounded-xl border border-n-weak min-h-[52rem]"
    >
      <woot-wizard
        class="hidden lg:block col-span-2 h-fit py-8 px-6"
        :global-config="globalConfig"
        :items="items"
      />
      <div class="col-span-6 overflow-hidden">
        <router-view />
      </div>
    </div>
  </div>
</template>
